// client/src/components/common/LoadingState.jsx
//
// Shared "we're fetching" state.
//
// Before this, every screen rolled its own: Dashboard used a bare <p> with a
// translated string, PostsList a hardcoded untranslated "Loading posts...",
// EventList something else again. Three mechanisms, inconsistent wording, and
// only some of them translated.
//
// Defaults to the generic common.loading ("Loading…") so a caller that passes
// nothing still gets a translated string — the failure mode is a generic
// message, never an untranslated one.
import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * @param {object}  props
 * @param {string}  [props.message]  Pre-resolved message. Prefer messageKey.
 * @param {string}  [props.messageKey='common.loading']  i18n key to translate.
 * @param {boolean} [props.inline=false]  Render inline (no block padding).
 * @param {string}  [props.className='']
 */
export default function LoadingState({
  message,
  messageKey = 'common.loading',
  inline = false,
  className = '',
}) {
  const { t } = useTranslation();
  const text = message || t(messageKey);

  return (
    <div
      className={`loading-state${inline ? ' loading-state--inline' : ''} ${className}`.trim()}
      // Announce politely: a loading message interrupting the user mid-sentence
      // is worse than telling them a moment later.
      role="status"
      aria-live="polite"
      aria-busy="true"
      data-testid="loading-state"
    >
      <span className="loading-state__spinner" aria-hidden="true" />
      <span className="loading-state__text">{text}</span>
    </div>
  );
}
